import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaTrophy,
  FaFire,
  FaCrosshairs,
  FaCode,
  FaCrown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { getAvatarSrc } from '../utils/avatar.ts';
import { API_BASE } from '../utils/apiEndpoints';

interface LeaderboardEntry {
  id: number;
  username: string;
  avatar: string | null;
  points: number;
  solved: number;
  accuracy: number;
  streak: number;
  maxStreak: number;
  activity: number[];
  lastActivityDate: string | null;
}

interface UserProfile {
  rank: number | null;
  solvedStats?: { total: number };
}

type PeriodFilter = 'all' | 'month' | 'week';
type CategoryFilter = 'algorithms' | 'sql' | 'ml';

const PAGE_SIZE = 10;
const RATING_MILESTONES = [500, 1000, 1500, 2000, 3000, 5000, 7500, 10000];

const COPY = {
  ru: {
    subtitle: 'Соревнуйтесь, отслеживайте прогресс и поднимайтесь выше среди лучших участников Sciplex.',
    titleAccent: 'лидеров',
    periodAll: 'Общий рейтинг',
    periodMonth: 'За месяц',
    periodWeek: 'За неделю',
    catAlgorithms: 'Алгоритмы',
    catSql: 'SQL',
    catMl: 'ML',
    rating: 'Рейтинг',
    solved: 'Решено задач',
    accuracy: 'Точность',
    streak: 'Серия',
    activity: 'Активность',
    thisIsYou: 'Это вы',
    topSolvers: 'Топ решателей',
    ratingLabel: 'Рейтинг',
    yourStats: 'Ваша статистика',
    statRank: 'Текущее место',
    statRating: 'Рейтинг',
    statSolved: 'Решено задач',
    statAccuracy: 'Точность',
    statStreak: 'Макс. серия',
    statLastActivity: 'Последняя активность',
    nextGoal: 'Ближайшая цель',
    nextGoalPrefix: 'До следующего рейтингового диапазона',
    pointsLeft: 'очков осталось',
    cardWeeklyTitle: 'Лучший рейтинг недели',
    cardWeeklyDesc: 'Максимальный рейтинг, достигнутый на этой неделе',
    cardAccuracyTitle: 'Средняя точность топ-100',
    cardAccuracyDesc: 'Средняя точность среди топ-100 участников',
    cardSectionTitle: 'Самый активный раздел',
    cardSectionDesc: 'Наибольшее количество решённых задач на этой неделе',
    today: 'Сегодня',
    loginHint: 'Войдите, чтобы видеть свою статистику',
  },
  en: {
    subtitle: 'Compete, track your progress, and climb higher among the best Sciplex participants.',
    titleAccent: 'board',
    periodAll: 'All-time',
    periodMonth: 'This month',
    periodWeek: 'This week',
    catAlgorithms: 'Algorithms',
    catSql: 'SQL',
    catMl: 'ML',
    rating: 'Rating',
    solved: 'Tasks solved',
    accuracy: 'Accuracy',
    streak: 'Streak',
    activity: 'Activity',
    thisIsYou: 'This is you',
    topSolvers: 'Top solvers',
    ratingLabel: 'Rating',
    yourStats: 'Your statistics',
    statRank: 'Current rank',
    statRating: 'Rating',
    statSolved: 'Tasks solved',
    statAccuracy: 'Accuracy',
    statStreak: 'Max streak',
    statLastActivity: 'Last activity',
    nextGoal: 'Next goal',
    nextGoalPrefix: 'Until the next rating tier',
    pointsLeft: 'points left',
    cardWeeklyTitle: 'Best weekly rating',
    cardWeeklyDesc: 'Highest rating reached this week',
    cardAccuracyTitle: 'Top-100 average accuracy',
    cardAccuracyDesc: 'Average accuracy among the top 100 participants',
    cardSectionTitle: 'Most active section',
    cardSectionDesc: 'Most tasks solved this week',
    today: 'Today',
    loginHint: 'Sign in to see your statistics',
  },
} as const;

function formatNum(n: number, lang: string): string {
  return n.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US');
}

function formatLastActivity(dateStr: string | null | undefined, lang: string, todayLabel: string): string {
  if (!dateStr) return '—';
  const today = new Date();
  const todayKey = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`;
  if (dateStr === todayKey) return todayLabel;
  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  const parsed = new Date(`${dateStr}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

function nextMilestone(points: number): number {
  const found = RATING_MILESTONES.find((m) => m > points);
  return found ?? RATING_MILESTONES[RATING_MILESTONES.length - 1] + Math.ceil(points / 5000) * 5000;
}

function Sparkline({ data }: { data: number[] }) {
  const w = 80;
  const h = 24;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg className="lb-sparkline" viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polyline points={pts} fill="none" stroke="var(--nn-accent-soft)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SideSparkle({ tone }: { tone: 'gold' | 'teal' }) {
  return (
    <svg className={`lb-side-sparkle lb-side-sparkle--${tone}`} viewBox="0 0 16 16" width="15" height="15" aria-hidden>
      <path d="M8 0.5L9.35 6.65L15.5 8L9.35 9.35L8 15.5L6.65 9.35L0.5 8L6.65 6.65Z" fill="currentColor" />
    </svg>
  );
}

function LeaderboardStyles() {
  return (
    <style>{`
      .lb-page {
        --lb-teal: var(--nn-accent);
        --lb-teal-soft: var(--nn-accent-soft);
        --lb-gold: var(--nn-accent-premium);
        position: relative;
        min-height: 100vh;
        background: var(--nn-bg-page);
        padding: 8px 0 48px;
        overflow: hidden;
        font-family: var(--nn-font-sans);
      }

      .lb-container {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--nn-content-max);
        margin: 0 auto;
        padding: 0;
      }

      .lb-hero {
        padding: 8px 0 28px;
      }

      .lb-hero-head {
        margin-bottom: 28px;
      }

      .lb-hero-title {
        margin: 0 0 14px;
        font-size: clamp(1.65rem, 3.6vw, 2.5rem);
        font-weight: 700;
        line-height: 1.12;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--nn-text-strong);
      }

      .lb-hero-title-accent {
        background: linear-gradient(90deg, var(--lb-teal-soft) 0%, #6ee7d6 55%, var(--lb-teal) 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }

      .lb-hero-subtitle {
        margin: 0;
        max-width: 520px;
        font-size: 14px;
        line-height: 1.65;
        color: var(--nn-text-muted);
      }

      .lb-filters {
        display: flex;
        flex-wrap: wrap;
        gap: 12px 16px;
        align-items: center;
      }

      .lb-filter-group {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px;
        background: var(--nn-bg-card);
        border: 1px solid var(--nn-border-subtle);
        border-radius: 999px;
      }

      .lb-filter-btn {
        padding: 9px 18px;
        border: none;
        border-radius: 999px;
        background: transparent;
        color: var(--nn-text-muted);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s, color 0.15s;
        white-space: nowrap;
      }

      .lb-filter-btn:hover {
        color: var(--nn-text-secondary);
      }

      .lb-filter-btn--active {
        background: var(--lb-teal);
        color: var(--nn-text-on-accent);
        font-weight: 600;
      }

      .lb-cat-group {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .lb-cat-btn {
        padding: 8px 16px;
        border-radius: 999px;
        border: 1px solid var(--nn-border-light);
        background: var(--nn-bg-card);
        color: var(--nn-text-muted);
        font-size: 13px;
        cursor: pointer;
        transition: border-color 0.15s, color 0.15s, background 0.15s;
      }

      .lb-cat-btn:hover {
        border-color: var(--nn-border-teal);
        color: var(--nn-text-secondary);
      }

      .lb-cat-btn--active {
        border-color: var(--nn-border-teal);
        background: var(--nn-accent-10);
        color: var(--lb-teal-soft);
      }

      .lb-main {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 320px;
        gap: 20px;
        align-items: start;
        margin-bottom: 24px;
      }

      .lb-aside {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: 0;
      }

      .lb-table-card {
        background: var(--nn-bg-card);
        border: 1px solid var(--nn-border);
        border-radius: 14px;
        overflow: hidden;
        box-shadow: var(--nn-shadow-panel);
      }

      .lb-table-scroll {
        overflow-x: auto;
      }

      .lb-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        min-width: 700px;
      }

      .lb-col-rank,
      .lb-col-user,
      .lb-col-rating,
      .lb-col-solved,
      .lb-col-accuracy,
      .lb-col-streak,
      .lb-col-activity {
        width: calc(100% / 7);
      }

      .lb-th {
        padding: 14px 10px;
        text-align: center;
        font-size: 11px;
        font-weight: 600;
        color: var(--nn-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border-bottom: 1px solid var(--nn-border);
        background: var(--nn-bg-panel);
        vertical-align: middle;
      }

      .lb-th--user { text-align: left; }

      .lb-row {
        border-bottom: 1px solid var(--nn-border);
        transition: background 0.12s;
      }

      .lb-row:last-child { border-bottom: none; }
      .lb-row:hover { background: var(--nn-bg-hover); }

      .lb-row--self {
        background: var(--nn-accent-08);
        box-shadow: inset 0 0 0 1px var(--nn-border-teal), 0 0 24px var(--nn-accent-glow);
      }

      .lb-row--self:hover { background: var(--nn-accent-10); }

      .lb-td {
        padding: 12px 10px;
        font-size: 14px;
        color: var(--nn-text-primary);
        vertical-align: middle;
        text-align: center;
      }

      .lb-td--user {
        text-align: left;
      }

      .lb-td--num {
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        font-size: 13px;
      }

      .lb-td--activity {
        text-align: center;
      }

      .lb-td--rating-top1 { color: var(--lb-gold); }

      .lb-rank-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        font-size: 13px;
        font-weight: 700;
        color: var(--nn-text-muted);
      }

      .lb-rank-badge--1 {
        background: linear-gradient(135deg, #f0c66d 0%, #d7a84a 100%);
        color: #1a1208;
      }

      .lb-rank-badge--2 {
        background: linear-gradient(135deg, #d8d8d8 0%, #9a9a9a 100%);
        color: #1a1a1a;
      }

      .lb-rank-badge--3 {
        background: linear-gradient(135deg, #cd7f32 0%, #a0622a 100%);
        color: #fff;
      }

      .lb-user {
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        color: inherit;
        min-width: 0;
      }

      .lb-user:hover .lb-username { color: var(--lb-teal-soft); }

      .lb-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid var(--nn-border-light);
        flex-shrink: 0;
      }

      .lb-username {
        font-weight: 500;
        color: var(--nn-text-strong);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .lb-you-badge {
        flex-shrink: 0;
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        background: var(--nn-accent-18);
        color: var(--lb-teal-soft);
        border: 1px solid var(--nn-border-teal);
      }

      .lb-streak {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        justify-content: center;
      }

      .lb-streak-icon { color: #f59e42; font-size: 12px; }

      .lb-sparkline {
        display: block;
        width: 80px;
        height: 24px;
        margin: 0 auto;
      }

      .lb-row--gap td {
        text-align: center;
        color: var(--nn-text-muted);
        font-size: 14px;
        letter-spacing: 0.35em;
        padding: 10px 14px 6px;
        border-bottom: none;
      }

      .lb-row--pinned {
        border-top: 1px solid var(--nn-border);
      }

      .lb-empty, .lb-loading, .lb-error {
        text-align: center;
        padding: 48px 20px;
        color: var(--nn-text-muted);
        font-size: 14px;
      }

      .lb-error { color: var(--nn-error); }

      .lb-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 16px;
        border-top: 1px solid var(--nn-border);
      }

      .lb-page-btn {
        min-width: 34px;
        height: 34px;
        padding: 0 8px;
        border: 1px solid var(--nn-border-light);
        border-radius: 8px;
        background: transparent;
        color: var(--nn-text-muted);
        font-size: 13px;
        cursor: pointer;
        transition: border-color 0.12s, color 0.12s, background 0.12s;
      }

      .lb-page-btn svg {
        fill: var(--nn-text-muted);
      }

      .lb-page-btn:hover:not(:disabled) svg {
        fill: var(--nn-text-secondary);
      }

      .lb-page-btn--active svg {
        fill: var(--lb-teal-soft);
      }

      .lb-page-btn:hover:not(:disabled) {
        border-color: var(--nn-border-teal);
        color: var(--nn-text-secondary);
      }

      .lb-page-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }

      .lb-page-btn--active {
        border-color: var(--lb-teal);
        background: var(--nn-accent-12);
        color: var(--lb-teal-soft);
        font-weight: 600;
      }

      .lb-page-ellipsis {
        color: var(--nn-text-muted);
        font-size: 13px;
        padding: 0 4px;
      }

      .lb-side-card {
        background: var(--nn-bg-card);
        border: 1px solid var(--nn-border);
        border-radius: 16px;
        padding: 20px;
      }

      .lb-side-card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 16px;
      }

      .lb-side-title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--nn-text-strong);
        letter-spacing: 0.01em;
        line-height: 1.3;
      }

      .lb-side-sparkle {
        flex-shrink: 0;
        margin-top: 2px;
      }

      .lb-side-sparkle--gold {
        color: var(--lb-gold);
        filter: drop-shadow(0 0 6px rgba(215, 168, 74, 0.45));
      }

      .lb-side-sparkle--teal {
        color: var(--lb-teal-soft);
        filter: drop-shadow(0 0 6px var(--nn-accent-glow));
      }

      .lb-top-solver {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 14px;
      }

      .lb-top-avatar-wrap {
        position: relative;
        width: 56px;
        height: 56px;
        flex-shrink: 0;
      }

      .lb-top-avatar {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid rgba(215, 168, 74, 0.55);
        box-shadow: 0 0 16px rgba(215, 168, 74, 0.18);
      }

      .lb-top-crown {
        position: absolute;
        bottom: -2px;
        right: -3px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: var(--nn-bg-card);
        border: 1px solid var(--nn-border);
        color: var(--lb-gold);
        font-size: 10px;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.35));
      }

      .lb-top-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .lb-top-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--nn-text-strong);
        text-decoration: none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .lb-top-name:hover { color: var(--lb-teal-soft); }

      .lb-top-rating {
        font-size: 26px;
        font-weight: 700;
        color: var(--lb-gold);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
        letter-spacing: -0.02em;
      }

      .lb-top-rating-label {
        font-size: 12px;
        font-weight: 400;
        color: var(--nn-text-muted);
        line-height: 1.2;
      }

      .lb-stat-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .lb-stat-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        font-size: 13px;
        line-height: 1.35;
      }

      .lb-stat-label {
        color: var(--nn-text-muted);
        flex: 1;
        min-width: 0;
      }

      .lb-stat-value {
        font-weight: 600;
        color: var(--nn-text-strong);
        font-variant-numeric: tabular-nums;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
        text-align: right;
      }

      .lb-stat-value--accent { color: var(--lb-teal-soft); }

      .lb-login-hint {
        margin: 0;
        font-size: 13px;
        color: var(--nn-text-muted);
        line-height: 1.5;
      }

      .lb-goal-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 14px;
      }

      .lb-goal-label {
        margin: 0;
        flex: 1;
        font-size: 12px;
        color: var(--nn-text-muted);
        line-height: 1.45;
      }

      .lb-goal-remain {
        flex-shrink: 0;
        text-align: right;
      }

      .lb-goal-points {
        margin: 0;
        font-size: 28px;
        font-weight: 700;
        color: var(--lb-teal-soft);
        line-height: 1;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.02em;
      }

      .lb-goal-sub {
        display: block;
        font-size: 11px;
        font-weight: 400;
        color: var(--nn-text-muted);
        margin-top: 3px;
        line-height: 1.2;
      }

      .lb-progress-track {
        height: 6px;
        border-radius: 999px;
        background: var(--nn-bg-panel);
        overflow: hidden;
        margin-bottom: 8px;
      }

      .lb-progress-fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, var(--lb-teal) 0%, var(--lb-teal-soft) 100%);
        transition: width 0.3s ease;
      }

      .lb-progress-label {
        font-size: 12px;
        color: var(--nn-text-muted);
        font-variant-numeric: tabular-nums;
        text-align: left;
      }

      .lb-bottom-cards {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
      }

      .lb-info-card {
        background: var(--nn-bg-card);
        border: 1px solid var(--nn-border);
        border-radius: 14px;
        padding: 20px;
      }

      .lb-info-icon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        margin-bottom: 12px;
      }

      .lb-info-icon--gold {
        background: var(--nn-premium-cta-20);
        color: var(--lb-gold);
      }

      .lb-info-icon--teal {
        background: var(--nn-accent-12);
        color: var(--lb-teal-soft);
      }

      .lb-info-title {
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: 600;
        color: var(--nn-text-muted);
      }

      .lb-info-value {
        margin: 0 0 8px;
        font-size: 26px;
        font-weight: 700;
        color: var(--nn-text-strong);
        font-variant-numeric: tabular-nums;
      }

      .lb-info-value--teal { color: var(--lb-teal-soft); }

      .lb-info-desc {
        margin: 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--nn-text-muted);
      }

      @media (max-width: 1024px) {
        .lb-main { grid-template-columns: 1fr; }
        .lb-aside {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        }
      }

      @media (max-width: 768px) {
        .lb-bottom-cards { grid-template-columns: 1fr; }
        .lb-filters { flex-direction: column; align-items: stretch; }
        .lb-filter-group, .lb-cat-group { justify-content: center; }
      }
    `}</style>
  );
}

function buildPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  rank: number;
  lang: string;
  copy: (typeof COPY)['ru'];
  isSelf: boolean;
  showYouBadge: boolean;
}

function LeaderboardRow({ entry, rank, lang, copy, isSelf, showYouBadge }: LeaderboardRowProps) {
  const rankClass =
    rank === 1 ? 'lb-rank-badge--1' : rank === 2 ? 'lb-rank-badge--2' : rank === 3 ? 'lb-rank-badge--3' : '';

  return (
    <tr className={`lb-row${isSelf ? ' lb-row--self' : ''}${showYouBadge ? ' lb-row--pinned' : ''}`}>
      <td className="lb-td lb-td--rank lb-col-rank">
        {rank <= 3 ? (
          <span className={`lb-rank-badge ${rankClass}`}>{rank}</span>
        ) : (
          rank
        )}
      </td>
      <td className="lb-td lb-td--user lb-col-user">
        <Link to={`/profile/${encodeURIComponent(entry.username)}`} className="lb-user">
          <img src={getAvatarSrc(entry.avatar)} alt="" className="lb-avatar" />
          <span className="lb-username">{entry.username}</span>
          {showYouBadge && <span className="lb-you-badge">{copy.thisIsYou}</span>}
        </Link>
      </td>
      <td className={`lb-td lb-td--num lb-col-rating${rank === 1 ? ' lb-td--rating-top1' : ''}`}>
        {formatNum(entry.points, lang)}
      </td>
      <td className="lb-td lb-td--num lb-col-solved">{formatNum(entry.solved, lang)}</td>
      <td className="lb-td lb-td--num lb-col-accuracy">{entry.accuracy.toFixed(1)}%</td>
      <td className="lb-td lb-td--num lb-col-streak">
        <span className="lb-streak">
          <FaFire className="lb-streak-icon" aria-hidden />
          {entry.streak}
        </span>
      </td>
      <td className="lb-td lb-td--activity lb-col-activity">
        <Sparkline data={entry.activity} />
      </td>
    </tr>
  );
}

function LeaderboardPage() {
  const { t, currentLanguage } = useTranslation();
  const { user } = useAuth();
  const copy = COPY[currentLanguage === 'ru' ? 'ru' : 'en'];

  const [list, setList] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [category, setCategory] = useState<CategoryFilter>('algorithms');
  const [page, setPage] = useState(1);
  const [myProfile, setMyProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE}/api/leaderboard`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setList(data.leaderboard ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('leaderboard.unknownError'));
        setList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [t]);

  useEffect(() => {
    if (!user) {
      setMyProfile(null);
      return;
    }
    fetch(`${API_BASE}/api/profile/profile`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setMyProfile({ rank: data.rank ?? null, solvedStats: data.solvedStats });
      })
      .catch(() => setMyProfile(null));
  }, [user]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [list, page]);

  const topUser = list[0] ?? null;

  const myRank = useMemo(() => {
    if (myProfile?.rank != null) return myProfile.rank;
    if (!user) return null;
    const idx = list.findIndex((e) => e.username === user.username);
    return idx >= 0 ? idx + 1 : null;
  }, [list, user, myProfile]);

  const myEntry = useMemo(() => {
    if (!user) return null;
    return list.find((e) => e.username === user.username) ?? null;
  }, [list, user]);

  const selfOnCurrentPage = useMemo(
    () => Boolean(user && pageItems.some((e) => e.username === user.username)),
    [user, pageItems]
  );

  const showPinnedSelfRow = Boolean(user && myEntry && myRank != null && !selfOnCurrentPage);

  const myPoints = myEntry?.points ?? 0;
  const mySolved = myEntry?.solved ?? myProfile?.solvedStats?.total ?? 0;
  const myAccuracy = myEntry?.accuracy ?? 0;
  const myStreak = myEntry?.maxStreak ?? 0;

  const milestone = nextMilestone(myPoints);
  const prevMilestone = RATING_MILESTONES.filter((m) => m <= myPoints).pop() ?? 0;
  const progressPct = myPoints > 0 ? ((myPoints - prevMilestone) / (milestone - prevMilestone)) * 100 : 0;
  const pointsLeft = Math.max(0, milestone - myPoints);

  const top100Accuracy = useMemo(() => {
    const top = list.slice(0, 100);
    if (!top.length) return 0;
    const sum = top.reduce((acc, e) => acc + e.accuracy, 0);
    return sum / top.length;
  }, [list]);

  const categoryLabels: Record<CategoryFilter, string> = {
    algorithms: copy.catAlgorithms,
    sql: copy.catSql,
    ml: copy.catMl,
  };

  const titleBase = t('leaderboard.title');
  const titleParts = titleBase.split(' ');
  const titleFirst = titleParts.slice(0, -1).join(' ') || titleBase;
  const titleLast = titleParts.length > 1 ? titleParts[titleParts.length - 1] : copy.titleAccent;

  const pageNumbers = buildPageNumbers(page, totalPages);
  const lang = currentLanguage === 'ru' ? 'ru' : 'en';
  const todayLabel = `${copy.today}, ${new Date().toLocaleTimeString(lang === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit' })}`;
  const lastActivityLabel = formatLastActivity(myEntry?.lastActivityDate, lang, todayLabel);

  if (loading) {
    return (
      <div className="lb-page">
        <LeaderboardStyles />
        <div className="lb-container">
          <div className="lb-loading">{t('leaderboard.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lb-page">
        <LeaderboardStyles />
        <div className="lb-container">
          <div className="lb-error">
            {t('leaderboard.errorPrefix')} {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lb-page">
      <LeaderboardStyles />

      <div className="lb-container">
        <section className="lb-hero">
          <div className="lb-hero-head">
            <h1 className="lb-hero-title">
              {titleFirst}{' '}
              <span className="lb-hero-title-accent">{titleLast}</span>
            </h1>
            <p className="lb-hero-subtitle">{copy.subtitle}</p>
          </div>

          <div className="lb-filters">
            <div className="lb-filter-group" role="group" aria-label={copy.periodAll}>
              {(['all', 'month', 'week'] as PeriodFilter[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lb-filter-btn${period === key ? ' lb-filter-btn--active' : ''}`}
                  onClick={() => setPeriod(key)}
                >
                  {key === 'all' ? copy.periodAll : key === 'month' ? copy.periodMonth : copy.periodWeek}
                </button>
              ))}
            </div>
            <div className="lb-cat-group" role="group" aria-label={copy.catAlgorithms}>
              {(['algorithms', 'sql', 'ml'] as CategoryFilter[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`lb-cat-btn${category === key ? ' lb-cat-btn--active' : ''}`}
                  onClick={() => setCategory(key)}
                >
                  {categoryLabels[key]}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="lb-main">
          <div className="lb-table-card">
            <div className="lb-table-scroll">
              <table className="lb-table">
                <thead>
                  <tr>
                    <th className="lb-th lb-col-rank">{t('leaderboard.rank')}</th>
                    <th className="lb-th lb-th--user lb-col-user">{t('leaderboard.user')}</th>
                    <th className="lb-th lb-col-rating">{copy.rating}</th>
                    <th className="lb-th lb-col-solved">{copy.solved}</th>
                    <th className="lb-th lb-col-accuracy">{copy.accuracy}</th>
                    <th className="lb-th lb-col-streak">{copy.streak}</th>
                    <th className="lb-th lb-col-activity">{copy.activity}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="lb-empty">
                        {t('leaderboard.empty')}
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((entry, index) => {
                      const rank = (page - 1) * PAGE_SIZE + index + 1;
                      const isSelf = user?.username === entry.username;

                      return (
                        <LeaderboardRow
                          key={entry.id}
                          entry={entry}
                          rank={rank}
                          lang={lang}
                          copy={copy}
                          isSelf={isSelf}
                          showYouBadge={isSelf && !showPinnedSelfRow}
                        />
                      );
                    })
                  )}
                  {showPinnedSelfRow && myEntry && myRank != null && (
                    <>
                      <tr className="lb-row lb-row--gap" aria-hidden>
                        <td colSpan={7}>…</td>
                      </tr>
                      <LeaderboardRow
                        key={`self-${myEntry.id}`}
                        entry={myEntry}
                        rank={myRank}
                        lang={lang}
                        copy={copy}
                        isSelf
                        showYouBadge
                      />
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {list.length > PAGE_SIZE && (
              <nav className="lb-pagination" aria-label="Pagination">
                <button
                  type="button"
                  className="lb-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <FaChevronLeft />
                </button>
                {pageNumbers.map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`e-${i}`} className="lb-page-ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`lb-page-btn${page === p ? ' lb-page-btn--active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="lb-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  <FaChevronRight />
                </button>
              </nav>
            )}
          </div>

          <div className="lb-aside">
            {topUser && (
              <div className="lb-top-solvers lb-side-card">
                <div className="lb-side-card-head">
                  <h2 className="lb-side-title">{copy.topSolvers}</h2>
                  <SideSparkle tone="gold" />
                </div>
                <div className="lb-top-solver">
                  <div className="lb-top-avatar-wrap">
                    <img src={getAvatarSrc(topUser.avatar)} alt="" className="lb-top-avatar" />
                    <span className="lb-top-crown" aria-hidden>
                      <FaCrown />
                    </span>
                  </div>
                  <div className="lb-top-info">
                    <Link to={`/profile/${encodeURIComponent(topUser.username)}`} className="lb-top-name">
                      {topUser.username}
                    </Link>
                    <div className="lb-top-rating">{formatNum(topUser.points, lang)}</div>
                    <div className="lb-top-rating-label">{copy.ratingLabel}</div>
                  </div>
                </div>
              </div>
            )}

            <aside className="lb-sidebar lb-side-card">
              <div className="lb-side-card-head">
                <h2 className="lb-side-title">{copy.yourStats}</h2>
                <SideSparkle tone="teal" />
              </div>
              {user && myEntry ? (
                <ul className="lb-stat-list">
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statRank}</span>
                    <span className="lb-stat-value lb-stat-value--accent">
                      {myRank != null ? formatNum(myRank, lang) : '—'}
                    </span>
                  </li>
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statRating}</span>
                    <span className="lb-stat-value lb-stat-value--accent">{formatNum(myPoints, lang)}</span>
                  </li>
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statSolved}</span>
                    <span className="lb-stat-value">{formatNum(mySolved, lang)}</span>
                  </li>
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statAccuracy}</span>
                    <span className="lb-stat-value">{myAccuracy.toFixed(1)}%</span>
                  </li>
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statStreak}</span>
                    <span className="lb-stat-value">
                      <FaFire className="lb-streak-icon" aria-hidden />
                      {myStreak}
                    </span>
                  </li>
                  <li className="lb-stat-item">
                    <span className="lb-stat-label">{copy.statLastActivity}</span>
                    <span className="lb-stat-value">{lastActivityLabel}</span>
                  </li>
                </ul>
              ) : (
                <p className="lb-login-hint">{copy.loginHint}</p>
              )}
            </aside>

            {user && myEntry && (
              <div className="lb-next-goal lb-side-card">
                <div className="lb-side-card-head">
                  <h2 className="lb-side-title">{copy.nextGoal}</h2>
                </div>
                <div className="lb-goal-head">
                  <p className="lb-goal-label">
                    {copy.nextGoalPrefix} ({formatNum(milestone, lang)})
                  </p>
                  <div className="lb-goal-remain">
                    <p className="lb-goal-points">{formatNum(pointsLeft, lang)}</p>
                    <span className="lb-goal-sub">{copy.pointsLeft}</span>
                  </div>
                </div>
                <div className="lb-progress-track">
                  <div className="lb-progress-fill" style={{ width: `${Math.min(100, progressPct)}%` }} />
                </div>
                <div className="lb-progress-label">
                  {formatNum(myPoints, lang)} / {formatNum(milestone, lang)}
                </div>
              </div>
            )}
          </div>
        </div>

        <section className="lb-bottom-cards">
          <article className="lb-info-card">
            <div className="lb-info-icon lb-info-icon--gold">
              <FaTrophy aria-hidden />
            </div>
            <h3 className="lb-info-title">{copy.cardWeeklyTitle}</h3>
            <p className="lb-info-value">{topUser ? formatNum(topUser.points, lang) : '—'}</p>
            <p className="lb-info-desc">{copy.cardWeeklyDesc}</p>
          </article>
          <article className="lb-info-card">
            <div className="lb-info-icon lb-info-icon--teal">
              <FaCrosshairs aria-hidden />
            </div>
            <h3 className="lb-info-title">{copy.cardAccuracyTitle}</h3>
            <p className="lb-info-value">{list.length ? `${top100Accuracy.toFixed(1)}%` : '—'}</p>
            <p className="lb-info-desc">{copy.cardAccuracyDesc}</p>
          </article>
          <article className="lb-info-card">
            <div className="lb-info-icon lb-info-icon--teal">
              <FaCode aria-hidden />
            </div>
            <h3 className="lb-info-title">{copy.cardSectionTitle}</h3>
            <p className="lb-info-value lb-info-value--teal">{categoryLabels[category]}</p>
            <p className="lb-info-desc">{copy.cardSectionDesc}</p>
          </article>
        </section>
      </div>
    </div>
  );
}

export default LeaderboardPage;
